import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell
} from 'recharts';

const API_URL = process.env.REACT_APP_API_URL || 'https://app.solteros.it/speeddate/api';

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const fmtMoney = (v) => {
  const n = parseFloat(v);
  if (isNaN(n)) return '€ 0,00';
  return n.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
};

const fmtNum = (v) => {
  const n = parseInt(v, 10);
  if (isNaN(n)) return '0';
  return n.toLocaleString('it-IT');
};

const COLORS = ['#6366f1', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#ef4444', '#8b5cf6', '#14b8a6'];
const PIE_COLORS = ['#6366f1', '#f59e0b', '#10b981', '#ec4899', '#3b82f6', '#ef4444', '#8b5cf6', '#14b8a6'];

const KpiCard = ({ icon, label, value, color, subValue }) => (
  <div className="col-md-3 col-6 mb-3">
    <div className="card border-0 shadow-sm h-100">
      <div className="card-body d-flex align-items-center gap-3 p-3">
        <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 48, height: 48, backgroundColor: color + '20' }}>
          <i className={`${icon} text-white`} style={{ color: color + ' !important' }}></i>
        </div>
        <div className="overflow-hidden">
          <div className="text-muted small text-uppercase fw-bold" style={{ fontSize: '11px' }}>{label}</div>
          <div className="fw-bold h5 mb-0 text-truncate">{value}</div>
          {subValue && <div className="text-muted small">{subValue}</div>}
        </div>
      </div>
    </div>
  </div>
);

const ChartCard = ({ title, children, fullWidth }) => (
  <div className={fullWidth ? 'col-12 mb-4' : 'col-lg-6 col-xl-6 mb-4'}>
    <div className="card border-0 shadow-sm h-100">
      <div className="card-header bg-transparent border-0 pb-0 pt-3 px-4">
        <h6 className="fw-bold mb-0"><i className="fas fa-chart-line text-primary me-2"></i>{title}</h6>
      </div>
      <div className="card-body p-3" style={{ minHeight: 320 }}>
        {children}
      </div>
    </div>
  </div>
);

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [events, setEvents] = useState([]);
  const [cities, setCities] = useState([]);
  const [ticketTypes, setTicketTypes] = useState([]);

  // Filter state
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedEvent, setSelectedEvent] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedTicketType, setSelectedTicketType] = useState('');

  // Load filter options
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    const headers = { 'x-admin-token': token };

    // Load events
    axios.get(`${API_URL}/events`, { headers })
      .then(res => setEvents(res.data || []))
      .catch(() => {});

    // Load cities
    axios.get(`${API_URL}/cities`, { headers })
      .then(res => setCities(res.data || []))
      .catch(() => {});

    // Load ticket types
    axios.get(`${API_URL}/tickets`, { headers })
      .then(res => setTicketTypes(res.data || []))
      .catch(() => {});
  }, []);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);

    const params = new URLSearchParams();
    if (fromDate) params.set('from', fromDate);
    if (toDate) params.set('to', toDate);
    if (selectedEvent) params.set('eventId', selectedEvent);
    if (selectedCity) params.set('cityId', selectedCity);
    if (selectedTicketType) params.set('ticketTypeId', selectedTicketType);

    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(`${API_URL}/admin/dashboard/stats?${params.toString()}`, {
        headers: { 'x-admin-token': token }
      });
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, selectedEvent, selectedCity, selectedTicketType]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Gender pie data
  const genderData = data?.genderSplit
    ? [
        { name: 'Uomini', value: data.genderSplit.male, color: '#3b82f6' },
        { name: 'Donne', value: data.genderSplit.female, color: '#ec4899' },
        ...(data.genderSplit.unknown > 0 ? [{ name: 'Altro', value: data.genderSplit.unknown, color: '#94a3b8' }] : [])
      ].filter(d => d.value > 0)
    : [];

  const hasFilters = fromDate || toDate || selectedEvent || selectedCity || selectedTicketType;

  return (
    <div className="container-fluid px-4">
      {/* Page header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="fw-bold mb-0">
          <i className="fas fa-chart-pie text-primary me-2"></i>Dashboard Statistiche
        </h4>
        <button className="btn btn-primary btn-sm rounded-pill px-3" onClick={fetchStats} disabled={loading}>
          <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-sync'} me-1`}></i> Aggiorna
        </button>
      </div>

      {/* Filter bar */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body py-3">
          <div className="row g-2 align-items-end">
            <div className="col-md-2">
              <label className="form-label fw-bold small text-uppercase text-muted">Dal</label>
              <input type="date" className="form-control form-control-sm" value={fromDate} onChange={e => setFromDate(e.target.value)} />
            </div>
            <div className="col-md-2">
              <label className="form-label fw-bold small text-uppercase text-muted">Al</label>
              <input type="date" className="form-control form-control-sm" value={toDate} onChange={e => setToDate(e.target.value)} />
            </div>
            <div className="col-md-2">
              <label className="form-label fw-bold small text-uppercase text-muted">Evento</label>
              <select className="form-select form-select-sm" value={selectedEvent} onChange={e => setSelectedEvent(e.target.value)}>
                <option value="">Tutti</option>
                {events.map(ev => (
                  <option key={ev.id} value={ev.id}>{ev.name}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label fw-bold small text-uppercase text-muted">Città</label>
              <select className="form-select form-select-sm" value={selectedCity} onChange={e => setSelectedCity(e.target.value)}>
                <option value="">Tutte</option>
                {cities.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label fw-bold small text-uppercase text-muted">Tipo Biglietto</label>
              <select className="form-select form-select-sm" value={selectedTicketType} onChange={e => setSelectedTicketType(e.target.value)}>
                <option value="">Tutti</option>
                {ticketTypes.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              {hasFilters && (
                <button className="btn btn-outline-secondary btn-sm rounded-pill w-100" onClick={() => { setFromDate(''); setToDate(''); setSelectedEvent(''); setSelectedCity(''); setSelectedTicketType(''); }}>
                  <i className="fas fa-times me-1"></i> Azzera
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Loading / Error */}
      {loading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Caricamento...</span></div>
          <p className="text-muted mt-2">Elaborazione statistiche...</p>
        </div>
      )}

      {error && (
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-triangle me-2"></i>{error}
        </div>
      )}

      {!loading && !error && data && (
        <>
          {/* KPI Cards */}
          <div className="row g-3 mb-4">
            <KpiCard
              icon="fas fa-calendar-check"
              label="Totale Eventi"
              value={fmtNum(data.summary.totalEvents)}
              color="#6366f1"
            />
            <KpiCard
              icon="fas fa-ticket-alt"
              label="Biglietti Venduti"
              value={fmtNum(data.summary.ticketsSold)}
              color="#10b981"
            />
            <KpiCard
              icon="fas fa-users"
              label="Partecipanti Unici"
              value={fmtNum(data.summary.uniqueParticipants)}
              color="#f59e0b"
            />
            <KpiCard
              icon="fas fa-euro-sign"
              label="Incasso Lordo"
              value={fmtMoney(data.summary.totalGross)}
              color="#ec4899"
              subValue={`Fee: ${fmtMoney(data.summary.totalFee)} · Netto: ${fmtMoney(data.summary.totalNet)}`}
            />
          </div>

          {/* Charts Row 1 */}
          <div className="row">
            <ChartCard title="Andamento Vendite nel Tempo">
              {data.daily.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={data?.daily || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v) => fmtDate(v)} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => v.toLocaleString('it-IT')} />
                    <Tooltip
                      labelFormatter={(v) => fmtDate(v)}
                      formatter={(value, name) => {
                        if (name === 'gross') return [fmtMoney(value), 'Incasso Lordo'];
                        if (name === 'net') return [fmtMoney(value), 'Incasso Netto'];
                        if (name === 'transactions') return [value, 'Transazioni'];
                        return [value, name === 'tickets' ? 'Biglietti' : name];
                      }}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="tickets" stroke="#6366f1" strokeWidth={2} name="Biglietti" dot={false} />
                    <Line type="monotone" dataKey="transactions" stroke="#f59e0b" strokeWidth={2} strokeDasharray="4 4" name="Transazioni" dot={false} />
                    <Line type="monotone" dataKey="net" stroke="#10b981" strokeWidth={2} name="Incasso Netto" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="d-flex align-items-center justify-content-center h-100 text-muted">Nessun dato disponibile</div>
              )}
            </ChartCard>

            <ChartCard title="Distribuzione per Evento">
              {data.events.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={data.events.slice(0, 15)} layout="vertical" margin={{ left: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="eventName" tick={{ fontSize: 10 }} width={140} />
                    <Tooltip formatter={(value, name) => {
                      if (name === 'gross') return [fmtMoney(value), 'Incasso'];
                      return [value, 'Biglietti'];
                    }} />
                    <Legend />
                    <Bar dataKey="tickets" fill="#6366f1" name="Biglietti" radius={[0, 4, 4, 0]} />
                    <Bar dataKey="gross" fill="#ec4899" name="Incasso Lordo" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="d-flex align-items-center justify-content-center h-100 text-muted">Nessun dato disponibile</div>
              )}
            </ChartCard>
          </div>

          {/* Charts Row 2 */}
          <div className="row">
            <ChartCard title="Composizione per Tipo Biglietto">
              {data.byTicketType.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={data.byTicketType}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      dataKey="tickets"
                      nameKey="name"
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    >
                      {data.byTicketType.map((entry, idx) => (
                        <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name) => [value, name === 'tickets' ? 'Biglietti' : name]} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="d-flex align-items-center justify-content-center h-100 text-muted">Nessun dato disponibile</div>
              )}
            </ChartCard>

            <ChartCard title="Confronto M/F Partecipanti">
              {genderData.length > 0 ? (
                <div>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={genderData}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={90}
                        dataKey="value"
                        nameKey="name"
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      >
                        {genderData.map((entry, idx) => (
                          <Cell key={idx} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="d-flex justify-content-center gap-4 mt-2 small">
                    <div><span className="badge" style={{ backgroundColor: '#3b82f6' }}>&nbsp;</span> Uomini: {fmtNum(data.genderSplit.male)}</div>
                    <div><span className="badge" style={{ backgroundColor: '#ec4899' }}>&nbsp;</span> Donne: {fmtNum(data.genderSplit.female)}</div>
                    {data.genderSplit.unknown > 0 && (
                      <div><span className="badge" style={{ backgroundColor: '#94a3b8' }}>&nbsp;</span> Altro: {fmtNum(data.genderSplit.unknown)}</div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="d-flex align-items-center justify-content-center h-100 text-muted">Nessun dato disponibile</div>
              )}
            </ChartCard>
          </div>



          {/* Conversion Events Trend (full width) — grouped bar chart */}
          <div className="row">
            <ChartCard title="Andamento Conversioni" fullWidth>
              {data.dailyConversions?.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={data.dailyConversions} barGap={2} barCategoryGap="20%">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => fmtDate(v)} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip
                      labelFormatter={(v) => fmtDate(v)}
                      formatter={(value, name) => {
                        const labels = { Lead: '📝 Lead', AddToCart: '🛒 Add to Cart', InitiateCheckout: '💳 Checkout', Purchase: '💰 Acquisti' };
                        return [value, labels[name] || name];
                      }}
                    />
                    <Legend />
                    <Bar dataKey="Lead" fill="#f59e0b" name="Lead" />
                    <Bar dataKey="AddToCart" fill="#3b82f6" name="AddToCart" />
                    <Bar dataKey="InitiateCheckout" fill="#8b5cf6" name="InitiateCheckout" />
                    <Bar dataKey="Purchase" fill="#10b981" name="Purchase" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="d-flex align-items-center justify-content-center h-100 text-muted">Nessun dato disponibile</div>
              )}
            </ChartCard>
          </div>

          {/* Data Grid — Eventi */}
          <div className="row mt-2">
            <div className="col-12 mb-4">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-transparent border-0 pt-3 px-4">
                  <h6 className="fw-bold mb-0"><i className="fas fa-table text-primary me-2"></i>Dettaglio per Evento</h6>
                </div>
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0" style={{ fontSize: '14px' }}>
                      <thead className="table-light">
                        <tr>
                          <th className="ps-4 small text-uppercase fw-bold text-muted">Evento</th>
                          <th className="small text-uppercase fw-bold text-muted">Data</th>
                          <th className="small text-uppercase fw-bold text-muted">Città</th>
                          <th className="small text-uppercase fw-bold text-muted text-end">Biglietti</th>
                          <th className="small text-uppercase fw-bold text-muted text-end">Partecipanti</th>
                          <th className="small text-uppercase fw-bold text-muted text-end">Incasso Lordo</th>
                          <th className="small text-uppercase fw-bold text-muted text-end">Fee PayPal</th>
                          <th className="small text-uppercase fw-bold text-muted text-end pe-4">Netto</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.events.length === 0 ? (
                          <tr>
                            <td colSpan={8} className="text-center py-4 text-muted">
                              <i className="fas fa-inbox d-block fs-1 mb-2"></i>
                              Nessun evento trovato
                            </td>
                          </tr>
                        ) : data.events.map(ev => (
                          <tr key={ev.eventId}>
                            <td className="ps-4 fw-semibold">{ev.eventName}</td>
                            <td className="text-muted small">{fmtDate(ev.eventDate)}</td>
                            <td className="text-muted small">{ev.city}</td>
                            <td className="text-end fw-bold">{fmtNum(ev.tickets)}</td>
                            <td className="text-end">{fmtNum(ev.participants)}</td>
                            <td className="text-end">{fmtMoney(ev.gross)}</td>
                            <td className="text-end text-muted">{fmtMoney(ev.fee)}</td>
                            <td className="text-end fw-bold pe-4">{fmtMoney(ev.net)}</td>
                          </tr>
                        ))}
                        {data.events.length > 0 && (
                          <tr className="table-primary fw-bold">
                            <td colSpan={3} className="ps-4">Totale</td>
                            <td className="text-end">{fmtNum(data.events.reduce((s, e) => s + e.tickets, 0))}</td>
                            <td className="text-end">{fmtNum(data.events.reduce((s, e) => s + e.participants, 0))}</td>
                            <td className="text-end">{fmtMoney(data.events.reduce((s, e) => s + e.gross, 0))}</td>
                            <td className="text-end">{fmtMoney(data.events.reduce((s, e) => s + e.fee, 0))}</td>
                            <td className="text-end pe-4">{fmtMoney(data.events.reduce((s, e) => s + e.net, 0))}</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* City distribution mini-summary */}
          {data.byCity.length > 0 && (
            <div className="row mb-4">
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-transparent border-0 pt-3 px-4">
                    <h6 className="fw-bold mb-0"><i className="fas fa-map-marker-alt text-primary me-2"></i>Riepilogo per Città</h6>
                  </div>
                  <div className="card-body p-0">
                    <div className="table-responsive">
                      <table className="table table-hover align-middle mb-0" style={{ fontSize: '14px' }}>
                        <thead className="table-light">
                          <tr>
                            <th className="ps-4 small text-uppercase fw-bold text-muted">Città</th>
                            <th className="small text-uppercase fw-bold text-muted text-end">Biglietti</th>
                            <th className="small text-uppercase fw-bold text-muted text-end pe-4">Incasso Lordo</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.byCity.map(c => (
                            <tr key={c.name}>
                              <td className="ps-4 fw-semibold">{c.name}</td>
                              <td className="text-end">{fmtNum(c.tickets)}</td>
                              <td className="text-end pe-4">{fmtMoney(c.gross)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {!loading && !error && !data && (
        <div className="text-center py-5 text-muted">
          <i className="fas fa-chart-bar d-block fs-1 mb-2 opacity-50"></i>
          <p>Seleziona i filtri e clicca "Aggiorna" per visualizzare le statistiche.</p>
        </div>
      )}
    </div>
  );
}